<template>
  <DashboardPageLayout
    title="Staff members"
    :is-loading="isLoading"
    @change="onDateRangeChange"
  >
    <template #summary>
      <div class="text-muted small">
        {{ formattedDateRange }}
      </div>
      <div
        v-if="supervisorsOrdersByDay"
        class="font-weight-bold"
      >
        Total {{ ordersLabel.toLowerCase() }}: {{ $formatNumber(supervisorsOrdersByDay.total, 0) }}
      </div>
    </template>

    <template #default="{ isLoading: chartLoading }">
      <DashboardChartCard
        :title="`${ordersLabel} managed by supervisors`"
        type="line"
        card-class="mb-2"
        :is-loading="chartLoading"
        :has-data="chartHasData"
        :empty-text="`No ${ordersLabel.toLowerCase()} in the selected date range.`"
        :options="chartOptions"
        :series="chartSeries"
      />

      <DashboardChartCard
        :title="`${ordersLabel} share by supervisors`"
        type="pie"
        :height="360"
        :is-loading="chartLoading"
        :has-data="chartHasData"
        :empty-text="`No ${ordersLabel.toLowerCase()} in the selected date range.`"
        :options="pieChartOptions"
        :series="pieChartSeries"
      />
    </template>
  </DashboardPageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import DashboardPageLayout from '@/views/Components/Elements/DashboardPageLayout.vue'
import DashboardChartCard from '@/views/Components/Elements/DashboardChartCard.vue'
import dashboardPageMixin from '@/mixins/dashboardPageMixin'
import {
  buildDashboardChartOptions,
  buildDashboardChartViewFromStatistics,
} from '@/libs/dashboardChart'

const SUPERVISOR_COLORS = [
  '#4F439E',
  '#0d6efd',
  '#28c76f',
  '#ea5455',
  '#ff9f43',
  '#00cfe8',
  '#7367f0',
  '#82868b',
  '#9c27b0',
  '#009688',
]
const NO_SUPERVISOR_COLOR = '#000000'
const NO_SUPERVISOR_KEY = 'supervisor_none'

export default {
  components: {
    DashboardPageLayout,
    DashboardChartCard,
  },
  mixins: [dashboardPageMixin],
  data() {
    return {
      dashboardLoadErrorMessage: 'Can not load staff statistics',
    }
  },
  computed: {
    ...mapGetters({
      supervisorsOrdersByDay: 'dashboard/supervisorsOrdersByDay',
    }),
    ordersLabel() {
      return this.companyPurpose !== 'rent' ? 'Orders' : 'Bookings'
    },
    seriesConfig() {
      return (this.supervisorsOrdersByDay?.series_meta || []).map((item) => ({
        name: item.name,
        key: item.key,
        color: item.color || null,
      }))
    },
    chartHasData() {
      return this.seriesConfig.length > 0
        && (this.supervisorsOrdersByDay?.days || []).length > 0
        && Number(this.supervisorsOrdersByDay?.total || 0) > 0
    },
    chartView() {
      if (!this.seriesConfig.length) {
        return null
      }

      return buildDashboardChartViewFromStatistics({
        statistics: this.supervisorsOrdersByDay,
        selectedDateRange: this.selectedDateRange,
        series: this.seriesConfig,
      })
    },
    chartSeries() {
      return this.chartView?.series || []
    },
    chartColors() {
      let paletteIndex = 0

      return this.seriesConfig.map((item) => {
        if (item.key === NO_SUPERVISOR_KEY || item.color === NO_SUPERVISOR_COLOR) {
          return NO_SUPERVISOR_COLOR
        }
        if (item.color) {
          return item.color
        }
        const color = SUPERVISOR_COLORS[paletteIndex % SUPERVISOR_COLORS.length]
        paletteIndex += 1
        return color
      })
    },
    chartOptions() {
      return buildDashboardChartOptions({
        chartView: this.chartView,
        chartType: 'line',
        colors: this.chartColors,
        yAxisTitle: this.ordersLabel,
        yAxisFormatter: (value) => Math.round(value).toLocaleString(this.$getNumberLocale()),
        sharedTooltip: true,
      })
    },
    supervisorTotals() {
      const days = this.supervisorsOrdersByDay?.days || []
      return this.seriesConfig.map((item, index) => {
        const total = days.reduce((sum, day) => sum + Number(day[item.key] ?? 0), 0)
        return {
          name: item.name,
          key: item.key,
          total,
          color: this.chartColors[index],
        }
      }).filter((item) => item.total > 0)
    },
    pieChartSeries() {
      return this.supervisorTotals.map((item) => item.total)
    },
    pieChartOptions() {
      const labels = this.supervisorTotals.map((item) => item.name)
      const colors = this.supervisorTotals.map((item) => item.color)
      const total = this.pieChartSeries.reduce((sum, value) => sum + Number(value || 0), 0)

      return {
        colors,
        labels,
        legend: {
          position: 'bottom',
        },
        tooltip: {
          y: {
            formatter: (value) => {
              const amount = Number(value) || 0
              const percent = total > 0 ? Math.round((amount / total) * 100) : 0
              return `${amount.toLocaleString(this.$getNumberLocale())} (${percent}%)`
            },
          },
        },
      }
    },
  },
  methods: {
    ...mapActions({
      loadSupervisorsOrdersByDay: 'dashboard/loadSupervisorsOrdersByDay',
    }),
    fetchDashboardData(payload) {
      return this.loadSupervisorsOrdersByDay(payload)
    },
  },
}
</script>
